/**
 * Helper quản lý danh sách video tour do khách tham quan tự quay (Session Hub & Local Storage)
 * Tuân thủ chuẩn PRD v3.0: Lưu trữ nhẹ nhàng, tự dọn dẹp sau 30 ngày, tối đa 20 video gần nhất.
 */

export interface MyRecordedTourItem {
    uuid: string; // asset_uuid từ backend
    tourName: string;
    videoUrl: string;
    thumbnailUrl?: string | null;
    duration?: number; // Thời lượng tính bằng giây
    fileSize?: number; // Dung lượng tính bằng bytes
    createdAt: number; // Epoch timestamp (ms)
    shareUrl?: string;
    categoryUuid?: string;
    claimed?: boolean; // Đã để lại SĐT/Email claim hay chưa
    claimedContact?: string;
    recordingType?: 'autotour' | 'ai_interaction';
}

const STORAGE_KEY_PREFIX = 'sg3d_my_tour_recordings_';
const MAX_RECORDINGS_PER_PROJECT = 20;
const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

function getStorageKey(idPath?: string): string {
    const safePath = (idPath || 'default').replace(/[^a-zA-Z0-9_-]/g, '_');
    return `${STORAGE_KEY_PREFIX}${safePath}`;
}

/**
 * Đọc danh sách video đã quay của dự án từ localStorage
 */
export function getMyRecordedTours(idPath?: string): MyRecordedTourItem[] {
    if (typeof window === 'undefined' || !window.localStorage) {
        return [];
    }

    try {
        const key = getStorageKey(idPath);
        const raw = window.localStorage.getItem(key);
        if (!raw) return [];

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        const now = Date.now();
        // Lọc bỏ các mục không hợp lệ hoặc đã quá hạn 30 ngày
        const validItems: MyRecordedTourItem[] = parsed.filter((item: any) => {
            if (!item || typeof item !== 'object' || !item.uuid || !item.videoUrl) {
                return false;
            }
            const created = Number(item.createdAt) || 0;
            // Nếu item đã claim thì giữ lại, chưa claim thì quá 30 ngày tự dọn
            if (!item.claimed && now - created > EXPIRY_MS) {
                return false;
            }
            return true;
        });

        // Sắp xếp mới nhất lên đầu
        validItems.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        // Nếu có bản ghi hết hạn bị lọc bỏ, đồng bộ lại localStorage
        if (validItems.length !== parsed.length) {
            window.localStorage.setItem(key, JSON.stringify(validItems));
        }

        return validItems;
    } catch (err) {
        console.warn('[MyTourRecordingsStorage] Lỗi khi đọc localStorage:', err);
        return [];
    }
}

/**
 * Lưu hoặc cập nhật một video vào danh sách đã quay
 */
export function saveRecordedTour(
    idPath: string | undefined,
    item: Omit<MyRecordedTourItem, 'createdAt'> & { createdAt?: number }
): MyRecordedTourItem[] {
    if (typeof window === 'undefined' || !window.localStorage) {
        return [];
    }

    try {
        const key = getStorageKey(idPath);
        const existing = getMyRecordedTours(idPath);

        const newItem: MyRecordedTourItem = {
            ...item,
            createdAt: item.createdAt || Date.now()
        };

        // Lọc bỏ bản ghi trùng uuid nếu đã tồn tại để cập nhật
        const updated = [newItem, ...existing.filter((it) => it.uuid !== newItem.uuid)];

        // Cắt bớt nếu vượt quá số lượng tối đa
        const trimmed = updated.slice(0, MAX_RECORDINGS_PER_PROJECT);

        window.localStorage.setItem(key, JSON.stringify(trimmed));

        // Phát sự kiện cập nhật để các component (như AutoTourItem drawer) cập nhật ngay lập tức
        window.dispatchEvent(
            new CustomEvent('my-tour-recordings-updated', {
                detail: { idPath, recordings: trimmed, latest: newItem }
            })
        );

        return trimmed;
    } catch (err) {
        console.error('[MyTourRecordingsStorage] Lỗi khi lưu vào localStorage:', err);
        return getMyRecordedTours(idPath);
    }
}

/**
 * Xóa một video khỏi lịch sử xem cá nhân
 */
export function deleteRecordedTour(idPath: string | undefined, uuid: string): MyRecordedTourItem[] {
    if (typeof window === 'undefined' || !window.localStorage || !uuid) {
        return [];
    }

    try {
        const key = getStorageKey(idPath);
        const existing = getMyRecordedTours(idPath);
        const remaining = existing.filter((item) => item.uuid !== uuid);

        window.localStorage.setItem(key, JSON.stringify(remaining));

        window.dispatchEvent(
            new CustomEvent('my-tour-recordings-updated', {
                detail: { idPath, recordings: remaining, deletedUuid: uuid }
            })
        );

        return remaining;
    } catch (err) {
        console.error('[MyTourRecordingsStorage] Lỗi khi xóa video khỏi localStorage:', err);
        return getMyRecordedTours(idPath);
    }
}

/**
 * Đánh dấu video đã được gửi/claim qua Zalo/Email
 */
export function markTourAsClaimed(idPath: string | undefined, uuid: string, contact?: string): boolean {
    if (typeof window === 'undefined' || !window.localStorage || !uuid) {
        return false;
    }

    try {
        const key = getStorageKey(idPath);
        const existing = getMyRecordedTours(idPath);
        let found = false;

        const updated = existing.map((item) => {
            if (item.uuid === uuid) {
                found = true;
                return {
                    ...item,
                    claimed: true,
                    claimedContact: contact || item.claimedContact
                };
            }
            return item;
        });

        if (found) {
            window.localStorage.setItem(key, JSON.stringify(updated));
            window.dispatchEvent(
                new CustomEvent('my-tour-recordings-updated', {
                    detail: { idPath, recordings: updated, claimedUuid: uuid }
                })
            );
        }

        return found;
    } catch (err) {
        console.error('[MyTourRecordingsStorage] Lỗi khi cập nhật claim video:', err);
        return false;
    }
}

/**
 * Định dạng thời lượng (giây -> mm:ss)
 */
export function formatDuration(seconds?: number): string {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Định dạng dung lượng byte -> KB/MB
 */
export function formatFileSize(bytes?: number): string {
    const b = Number(bytes) || 0;
    if (b <= 0) return '0 MB';
    if (b < 1024 * 1024) {
        return `${(b / 1024).toFixed(1)} KB`;
    }
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Định dạng ngày giờ quay video thân thiện
 */
export function formatRecordedDate(timestamp: number): string {
    if (!timestamp) return '';
    try {
        const date = new Date(timestamp);
        const now = new Date();
        const isToday =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

        const timeStr = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        if (isToday) {
            return `Hôm nay, ${timeStr}`;
        }

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}, ${timeStr}`;
    } catch {
        return '';
    }
}
